import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  ComponentProps,
} from 'react';
import styled from 'styled-components';
import { noop, pxToRem } from '@gif/shared-global';
import { ChevronIcon as ChevronIconComponent } from '../../assets';

const ICON_HEIGHT = 24;

const Wrapper = styled.div`
  display: inline-flex;
  width: 100%;
`;

const Select = styled.select<{
  $isPlaceholderSelected?: boolean;
}>`
  display: inline-flex;
  padding: 0 ${pxToRem(ICON_HEIGHT)} 0 ${pxToRem(ICON_HEIGHT / 2)};
  align-items: center;
  appearance: none;
  word-break: normal;
  cursor: pointer;
  &:placeholder-shown {
    text-overflow: ellipsis;
  }
`;

const IconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  margin-left: -${pxToRem(ICON_HEIGHT)};
  pointer-events: none;
`;

const ChevronIcon = styled(ChevronIconComponent)`
  height: ${pxToRem(ICON_HEIGHT)};
`;

export interface DropdownOptions {
  value: string | number;
  label: string | number;
}

export interface DropdownProps extends Omit<ComponentProps<'select'>, 'ref'> {
  className?: string;
  options?: DropdownOptions[];
  placeholder?: string;
  hasError?: boolean;
}

export const Dropdown = forwardRef<HTMLSelectElement, DropdownProps>(
  (props, ref) => {
    const {
      className,
      options = [],
      placeholder,
      value,
      hasError,
      onChange = noop,
      ...rest
    } = props;
    const [isPlaceholderSelected, setIsPlaceholderSelected] = useState(
      placeholder !== '' && (value === '' || typeof value === 'undefined')
    );
    const hasMounted = useRef(false);
    useEffect(() => {
      if (!hasMounted.current) {
        return;
      }
      const isEmptyPlaceholder =
        placeholder === '' || typeof placeholder === 'undefined';
      if (isEmptyPlaceholder) {
        setIsPlaceholderSelected(false);
      }
      if (isEmptyPlaceholder || typeof value === 'undefined') {
        return;
      }
      setIsPlaceholderSelected(value === '');
    }, [value, placeholder]);
    useEffect(() => {
      hasMounted.current = true;
      return () => {
        hasMounted.current = false;
      };
    }, []);
    const onChangeHandler = useCallback(
      (event: ChangeEvent<HTMLSelectElement>) => {
        setIsPlaceholderSelected(false);
        onChange(event);
      },
      [onChange]
    );
    return (
      <Wrapper className={className}>
        <Select
          {...rest}
          ref={ref}
          aria-invalid={hasError}
          $isPlaceholderSelected={isPlaceholderSelected}
          {...(typeof value === 'undefined' && { defaultValue: '' })} // allow for placeholder to work on uncontrolled component
          {...(typeof value !== 'undefined' && { value })} // use the `value` prop only for controlled components
          value={value}
          onChange={onChangeHandler}
        >
          {placeholder && (
            <option key="" value="" disabled>
              {placeholder}
            </option>
          )}
          {options?.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <IconContainer>
          <ChevronIcon />
        </IconContainer>
      </Wrapper>
    );
  }
);

export default Dropdown;
